import { useEffect, useState } from 'react';
import { logger } from '@/app/logger';
import type { ArticleRow } from '../../../shared/schemas/article';
import {
  fetchPublishedArticles,
  peekPublishedArticles,
} from '../lib/articlesPublicApi';

const log = logger.create('useCachedPublishedArticles');

interface CachedPublishedArticlesState {
  data: ArticleRow[] | null;
  loading: boolean;
  error: string | null;
}

function initialState(fetchUrl: string | undefined): CachedPublishedArticlesState {
  if (!fetchUrl) {
    return { data: null, loading: false, error: null };
  }

  const cached = peekPublishedArticles(fetchUrl);
  return {
    data: cached ?? null,
    loading: cached === undefined,
    error: null,
  };
}

export function useCachedPublishedArticles(
  fetchUrl: string | undefined,
): CachedPublishedArticlesState {
  const [state, setState] = useState<CachedPublishedArticlesState>(() =>
    initialState(fetchUrl),
  );

  useEffect(() => {
    if (!fetchUrl) {
      setState({ data: null, loading: false, error: null });
      return;
    }

    const cached = peekPublishedArticles(fetchUrl);
    if (cached !== undefined) {
      setState({ data: cached, loading: false, error: null });
      return;
    }

    let cancelled = false;
    setState({ data: null, loading: true, error: null });

    fetchPublishedArticles(fetchUrl)
      .then((data) => {
        if (cancelled) return;
        setState({ data, loading: false, error: null });
      })
      .catch((err) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Request failed';
        log.error('fetch', 'Cached published articles fetch failed', { fetchUrl, message });
        setState({ data: null, loading: false, error: message });
      });

    return () => {
      cancelled = true;
    };
  }, [fetchUrl]);

  return state;
}
